import { useState } from "react";
import { Heart, MoreHorizontal, Share2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { Experiment } from "@/mocks/experiments";

interface ExperimentCardProps {
  experiment: Experiment;
}

const categoryColorMap: Record<string, string> = {
  "催化材料": "bg-primary text-primary-foreground",
  "生物材料": "bg-teal-500 text-white",
  "新能源": "bg-sky-500 text-white",
  "催化化学": "bg-indigo-500 text-white",
  "医学处理": "bg-orange-500 text-white",
  "神经科学": "bg-violet-500 text-white",
  "公共安全": "bg-primary text-primary-foreground",
  "材料科学": "bg-slate-500 text-white",
  "信号处理": "bg-amber-500 text-white",
  "生物工程": "bg-teal-600 text-white",
  "量子物理": "bg-purple-500 text-white",
  "基因组学": "bg-rose-500 text-white",
};

export default function ExperimentCard({ experiment }: ExperimentCardProps) {
  const [liked, setLiked] = useState(false);
  const navigate = useNavigate();
  const tagCls = categoryColorMap[experiment.category] ?? "bg-primary text-primary-foreground";

  return (
    <div
      className="group overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
      onClick={() => navigate(`/experiments/${experiment.id}`)}
    >
      <div className="relative h-44 overflow-hidden bg-muted">
        <img
          src={experiment.coverImage}
          alt={experiment.title}
          className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.02]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
        <div className="absolute bottom-3 left-3">
          <span className={`inline-block rounded-full px-2.5 py-1 text-[11px] font-medium ${tagCls}`}>{experiment.category}</span>
        </div>
      </div>

      <div className="p-4">
        <h3 className="line-clamp-3 min-h-[72px] text-sm font-semibold leading-6 tracking-tight text-foreground">{experiment.title}</h3>

        <div className="mt-4 flex items-center justify-between border-t border-border pt-3 text-xs text-muted-foreground">
          <span>{experiment.createdAt}</span>
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-accent hover:text-accent-foreground"
              title="分享"
            >
              <Share2 className="h-4 w-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLiked(!liked);
              }}
              className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${liked ? "text-rose-500" : "hover:bg-accent hover:text-accent-foreground"}`}
              title="收藏"
            >
              <Heart className={`h-4 w-4 ${liked ? "fill-current" : ""}`} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-accent hover:text-accent-foreground"
              title="更多"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
